import { useMemo } from "react";
import {
  UseMutationResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import {
  createPortfolio,
  deletePortfolio,
  getPortfolios,
  updatePortfolio,
} from "apps/project-iq/api";
import { IPortfolio } from "apps/project-iq/models/IPortfolio";

export const PORTFOLIOS_QUERY_KEY = "portfolios";

export interface IUsePortfoliosResult {
  portfolios: IPortfolio[];
  isLoading: boolean;
  isError: boolean;
  createMutation: UseMutationResult<IPortfolio, Error, string>;
  updateMutation: UseMutationResult<IPortfolio, Error, IPortfolio>;
  deleteMutation: UseMutationResult<unknown, Error, string>;
}

export const usePortfolios = (): IUsePortfoliosResult => {
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery<IPortfolio[], Error>({
    queryKey: [PORTFOLIOS_QUERY_KEY],
    queryFn: () => getPortfolios(),
  });

  const portfolios = useMemo(() => data ?? [], [data]);

  const createMutation = useMutation<IPortfolio, Error, string>(
    (label: string) => createPortfolio({ label }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([PORTFOLIOS_QUERY_KEY]);
      },
    },
  );

  const updateMutation = useMutation<IPortfolio, Error, IPortfolio>(
    ({ id, label }: IPortfolio) => updatePortfolio(id, { label }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([PORTFOLIOS_QUERY_KEY]);
      },
    },
  );

  const deleteMutation = useMutation<unknown, Error, string>(
    (id: string) => deletePortfolio(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([PORTFOLIOS_QUERY_KEY]);
      },
    },
  );

  return {
    portfolios,
    isLoading,
    isError,
    createMutation,
    updateMutation,
    deleteMutation,
  };
};
